import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { DiscountFormValues } from '../components/discounts/form/form-constants';
import { ScopeItem } from '../components/form-fields/scope-selector/scope-selector-constants';
import {
  DiscountUsage,
  GetRevenueSharesQuery,
  PackageType,
  ScopeableType,
  ScopeInput,
} from '../types';
import { capitalizeFirstLetterAndSpaceSnakeString } from './bills-utils';
import { ActiveStatus } from './contract-utils';
import { GqlNetworkStatus } from './graphql-utils';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);

export enum DiscountType {
  billingCaps = 'billing_caps',
  engagementBillingCaps = 'engagement_billing_caps',
  innovationCredits = 'innovation_credits',
  volumeBasedDiscounts = 'volume_based_discounts',
  programAccess = 'program_access',
  freeBillableActivities = 'free_billable_activities',
  programCapCredits = 'program_cap_credits',
}

export enum InvalidProgramDiscounts {
  'free_billable_activities',
}

export enum InvalidPackagesForDiscounts {
  'in_person_visit',
}

export interface DiscountTypeOption {
  value: keyof DiscountType;
  displayName: string;
}

export const DiscountTypeSelectOptions = (
  Object.keys(DiscountType) as (keyof typeof DiscountType)[]
)
  .map(key => ({
    value: DiscountType[key],
    displayName: DiscountType[key].replaceAll('_', ' '),
  }))
  .filter(x => !['volume_based_discounts'].includes(x.value));

/**
 * Converts the form's scope items into the mutation input shape. The Global
 * scope carries a null `scopeableId` and must be sent; targeted scopes missing
 * a numeric `scopeableId` are incomplete and dropped.
 */
export const toScopeInputs = (scopes?: ScopeItem[]): ScopeInput[] | undefined =>
  scopes
    ?.filter(
      s =>
        s.scopeableType === ScopeableType.Global ||
        typeof s.scopeableId === 'number',
    )
    .map(s => ({
      scopeableType: s.scopeableType,
      scopeableId:
        s.scopeableType === ScopeableType.Global
          ? null
          : (s.scopeableId as number),
    }));

/**
 * Adapts persisted scopes to what the edit forms treat as user-selectable
 * scopes. A persisted global definition carries an explicit scope row
 * (`scopeableType: Global`, `scopeableId: null`), but the form represents
 * "global" as the Global radio with an empty `scopes` array — selecting Global
 * clears scopes and the validation rejects any selections under it. Loading the
 * persisted Global row straight into the form would therefore surface it as a
 * selection and trip "Global scope cannot include selections", so it is dropped
 * here. Definitions with no scope rows at all are also global and load cleanly.
 */
export const toEditableScopes = (scopes?: ScopeItem[]): ScopeItem[] =>
  (scopes ?? []).filter(s => s.scopeableType !== ScopeableType.Global);

export const getEditDiscountAmount = (discountToEdit: IDiscount): number => {
  switch (discountToEdit.discountType) {
    case DiscountType.billingCaps:
    case DiscountType.engagementBillingCaps:
      return discountToEdit.discountDetails[1].discount;
    case DiscountType.programAccess:
      return discountToEdit.discountDetails[0].discount;
    case DiscountType.freeBillableActivities:
      return discountToEdit.discountDetails[0].discount;
    case DiscountType.innovationCredits:
      const subscriptionTier =
        discountToEdit.discountDetails[0].subscriptionTier;
      return (subscriptionTier[1] ?? 0) - 1;
    default:
      return discountToEdit.discountDetails[1].discount;
  }
};

export const getEditDefaultValues = (
  discountToEdit: IDiscount,
): DiscountFormValues => {
  const discountAmount = getEditDiscountAmount(discountToEdit);
  const existingScopes: ScopeItem[] = toEditableScopes(discountToEdit.scopes);
  const inferredScopeableType: ScopeableType =
    existingScopes.length > 0
      ? existingScopes[0].scopeableType
      : ScopeableType.Global;

  return {
    clientId: discountToEdit.clientId ?? 0,
    discountType: discountToEdit.discountType as DiscountType,
    startDate: dayjs(discountToEdit.startDate).toDate(),
    endDate: dayjs(discountToEdit.endDate).isValid()
      ? dayjs(discountToEdit.endDate).toDate()
      : null,
    discount: discountAmount,
    suppressEmptyCharges: discountToEdit.suppressEmptyCharges,
    scopeableType: inferredScopeableType,
    scopes: existingScopes,
  };
};

export enum ProgramTypeDisplay {
  'Chronic & Acute',
  'Chronic',
  'Acute',
}

export const PackageTypeDisplay = (
  packageId: number,
  availablePackages: PackageType[],
): string => {
  if (packageId === 0) {
    return 'All Packages';
  }
  const selectedPackage = availablePackages.find(({ id }) => id === packageId);
  return capitalizeFirstLetterAndSpaceSnakeString(selectedPackage?.name ?? '');
};

export interface PackageDiscountDisplay {
  value: string;
  displayName: string;
}

export const PackageSelectionOptions = (
  availablePackages: PackageType[],
): PackageDiscountDisplay[] =>
  availablePackages.map(({ id, name }) => ({
    value: id.toString(),
    displayName: capitalizeFirstLetterAndSpaceSnakeString(name),
  }));

export enum ProgramType {
  'global',
  'chronic',
  'acute',
}

export interface IDiscountUsage
  extends Partial<DiscountUsage>,
    GqlNetworkStatus {}

export interface IDiscountDetail {
  discount: number;
  discountDetailId: number;
  milestoneDiscount: object;
  subscriptionTier: (number | null | undefined)[];
  tier: number;
  discountUsage: IDiscountUsage;
}
export interface IDiscount {
  clientId?: number | null;
  discountDefinitionId: number;
  discountType: string;
  endDate?: string | null;
  insurerId?: number | null;
  name?: string | null;
  partnershipId?: number | null;
  scopes?: ScopeItem[];
  startDate: string;
  discountDetails: IDiscountDetail[];
  suppressEmptyCharges: boolean;
  status: ActiveStatus;
}

export interface DiscountOverlapReturn {
  isOverlap: boolean;
  discountDefinitionId: number;
}

const hasSamePackage = (
  discount: IDiscount,
  selectedPackageId: number | null,
): boolean => {
  // packageId 0 ("All Packages") overlaps with every package selection.
  if (selectedPackageId === 0) {
    return true;
  }
  return (discount.scopes ?? []).some(
    s =>
      s.scopeableType === ScopeableType.Global ||
      (s.scopeableType === ScopeableType.Package &&
        s.scopeableId === selectedPackageId),
  );
};

export interface DiscountOverlap {
  startDateOverlaps: boolean;
  endDateOverlaps: boolean;
}

export const hasDiscountDateOverlap = (
  discount: IDiscount,
  contractStart: string | Date,
  contractEnd?: string | Date | null,
): boolean => {
  const discountStart = dayjs(discount.startDate);
  const discountEnd = discount.endDate ? dayjs(discount.endDate) : null;
  const cStart = dayjs(contractStart);
  const cEnd = contractEnd ? dayjs(contractEnd) : null;

  if (cEnd && discountEnd) {
    return discountStart.isBefore(cEnd) && discountEnd.isAfter(cStart);
  }
  if (cEnd) {
    return discountStart.isBefore(cEnd);
  }
  if (discountEnd) {
    return discountEnd.isAfter(cStart);
  }
  return true;
};

export const discountAppliesToPackage = (
  discount: IDiscount,
  packageId: number,
  productOfferingIds?: Set<number>,
): boolean =>
  (discount.scopes ?? []).some(s => {
    if (s.scopeableType === ScopeableType.Global) return true;
    if (
      s.scopeableType === ScopeableType.Package &&
      s.scopeableId === packageId
    ) {
      return true;
    }
    if (
      s.scopeableType === ScopeableType.ProductOffering &&
      s.scopeableId != null &&
      productOfferingIds?.has(s.scopeableId)
    ) {
      return true;
    }
    return false;
  });

export const getApplicableDiscounts = (
  discounts: IDiscount[] | undefined,
  packageId: number,
  contractStart: string | Date,
  contractEnd?: string | Date | null,
  productOfferingIds?: Set<number>,
): IDiscount[] => {
  if (!discounts) return [];
  return discounts.filter(
    d =>
      discountAppliesToPackage(d, packageId, productOfferingIds) &&
      hasDiscountDateOverlap(d, contractStart, contractEnd),
  );
};

export const checkDiscountOverlap = (
  startDate: Date,
  endDate: Date | null,
  selectedDiscountType: DiscountType,
  selectedPackageId: number | null,
  currentDiscounts: IDiscount[],
  editId?: number,
): DiscountOverlap => {
  let startDateOverlaps = false;
  let endDateOverlaps = false;

  if (editId) {
    currentDiscounts = currentDiscounts.filter(
      discount => discount.discountDefinitionId !== editId,
    );
  }

  currentDiscounts = currentDiscounts
    .filter(d => d.discountType === selectedDiscountType)
    .filter(d => hasSamePackage(d, selectedPackageId))
    .sort((a, b) => dayjs(a.startDate).diff(dayjs(b.startDate)));

  const selectedStart = dayjs(startDate);
  const selectedEnd = endDate ? dayjs(endDate) : null;

  for (const discount of currentDiscounts) {
    const discountStart = dayjs(discount.startDate);
    const discountEnd = discount.endDate ? dayjs(discount.endDate) : null;

    if (selectedStart && selectedEnd) {
      if (
        discountStart.isBetween(selectedStart, selectedEnd, 'day', '[]') &&
        discountEnd?.isBetween(selectedStart, selectedEnd, 'day', '[]')
      ) {
        startDateOverlaps = true;
        endDateOverlaps = true;
        break;
      }
      startDateOverlaps =
        (discountEnd &&
          selectedStart.isBetween(discountStart, discountEnd, 'day', '[]')) ||
        (!discountEnd && selectedStart.isSameOrAfter(discountStart, 'day'));
      endDateOverlaps =
        (discountEnd &&
          selectedEnd.isBetween(discountStart, discountEnd, 'day', '[]')) ||
        (!discountEnd && selectedEnd.isSameOrAfter(discountStart, 'day'));
    } else {
      startDateOverlaps =
        (discountEnd &&
          selectedStart.isBetween(discountStart, discountEnd, 'day', '[]')) ||
        (!discountEnd && selectedStart.isSameOrAfter(discountStart, 'day'));
      endDateOverlaps =
        selectedStart.isSameOrBefore(discountStart, 'day') ||
        (!discountEnd && selectedStart.isSameOrAfter(discountStart, 'day'));
    }

    if (startDateOverlaps || endDateOverlaps) {
      break;
    }
  }

  return {
    startDateOverlaps,
    endDateOverlaps,
  };
};

/**
 * Overlap check for VBDs. Unlike checkDiscountOverlap, package is NOT filtered —
 * all packages overlap with all others for VBDs. Filters by partnershipId or clientId.
 */
export const checkVbdOverlap = (
  startDate: string,
  endDate: string | null,
  partnershipId: number | null,
  clientId: number | null,
  existingVbds: IDiscount[],
  editId?: number,
): DiscountOverlap => {
  let startDateOverlaps = false;
  let endDateOverlaps = false;

  let candidates = editId
    ? existingVbds.filter(d => d.discountDefinitionId !== editId)
    : existingVbds;

  // ACCOUNTS-AND-PLAN-MIGRATION: partnershipId/clientId are client/partnership-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
  if (partnershipId != null) {
    candidates = candidates.filter(d => d.partnershipId === partnershipId);
  } else if (clientId != null) {
    candidates = candidates.filter(d => d.clientId === clientId);
  } else {
    return { startDateOverlaps: false, endDateOverlaps: false };
  }

  candidates = candidates.sort((a, b) =>
    dayjs(a.startDate).diff(dayjs(b.startDate)),
  );

  // Use dayjs string parsing (local time) to avoid new Date() UTC-offset issues
  const selectedStart = dayjs(startDate);
  const selectedEnd = endDate ? dayjs(endDate) : null;

  for (const vbd of candidates) {
    const vbdStart = dayjs(vbd.startDate);
    const vbdEnd = vbd.endDate ? dayjs(vbd.endDate) : null;

    if (selectedStart && selectedEnd) {
      if (
        vbdStart.isBetween(selectedStart, selectedEnd, 'day', '[]') &&
        vbdEnd?.isBetween(selectedStart, selectedEnd, 'day', '[]')
      ) {
        startDateOverlaps = true;
        endDateOverlaps = true;
        break;
      }
      startDateOverlaps =
        (vbdEnd && selectedStart.isBetween(vbdStart, vbdEnd, 'day', '[]')) ||
        (!vbdEnd && selectedStart.isSameOrAfter(vbdStart, 'day'));
      endDateOverlaps =
        (vbdEnd && selectedEnd.isBetween(vbdStart, vbdEnd, 'day', '[]')) ||
        (!vbdEnd && selectedEnd.isSameOrAfter(vbdStart, 'day'));
    } else {
      startDateOverlaps =
        (vbdEnd && selectedStart.isBetween(vbdStart, vbdEnd, 'day', '[]')) ||
        (!vbdEnd && selectedStart.isSameOrAfter(vbdStart, 'day'));
      endDateOverlaps =
        selectedStart.isSameOrBefore(vbdStart, 'day') ||
        (!vbdEnd && selectedStart.isSameOrAfter(vbdStart, 'day'));
    }

    if (startDateOverlaps || endDateOverlaps) {
      break;
    }
  }

  return { startDateOverlaps, endDateOverlaps };
};

export const formatSubscriptionTier = (
  subscriptionTier: (number | null | undefined)[],
): string => {
  const lower = subscriptionTier[0] ?? 1;
  const upper = subscriptionTier[1] ? ` - ${subscriptionTier[1]}` : '+';
  return `${lower}${upper}`;
};
export interface IRevenueShareDetail {
  revenueShareDetailId: number;
  tier: number;
  discount: number;
  subscriptionTier: (number | null | undefined)[];
}

export interface IRevenueShare {
  revenueShareDefinitionId: number;
  clientId?: number | null;
  partnershipId?: number | null;
  scopes?: ScopeItem[];
  name?: string | null;
  applyToPrice: boolean;
  effectiveYear: number;
  status: ActiveStatus;
  details: IRevenueShareDetail[];
}

export const computeYearStatus = (effectiveYear: number): ActiveStatus => {
  const currentYear = new Date().getFullYear();
  if (effectiveYear === currentYear) return ActiveStatus.Active;
  if (effectiveYear > currentYear) return ActiveStatus.Future;
  return ActiveStatus.Past;
};

export type RevenueShareItem =
  GetRevenueSharesQuery['getRevenueShares'][number];

// ACCOUNTS-AND-PLAN-MIGRATION: clientId/partnershipId are client/partnership-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration. Covers the comparison here and the record-building in groupRevenueShares below.
export const checkRevenueShareOverlap = (
  formValues: {
    effectiveYear: number | null;
    clientId: number | null;
    partnershipId: number | null;
  },
  existing: IRevenueShare[],
  editDefinitionId?: number,
): boolean => {
  const candidates = editDefinitionId
    ? existing.filter(r => r.revenueShareDefinitionId !== editDefinitionId)
    : existing;

  return candidates.some(
    r =>
      r.effectiveYear === formValues.effectiveYear &&
      (r.clientId ?? null) === (formValues.clientId ?? null) &&
      (r.partnershipId ?? null) === (formValues.partnershipId ?? null),
  );
};

export const groupRevenueShares = (
  items: RevenueShareItem[],
): IRevenueShare[] => {
  const grouped = new Map<number, IRevenueShare>();

  for (const item of items) {
    const existing = grouped.get(item.revenueShareDefinitionId);
    const detail: IRevenueShareDetail = {
      revenueShareDetailId: item.revenueShareDetailId,
      tier: item.tier,
      discount: item.discount,
      subscriptionTier: item.subscriptionTier,
    };

    if (existing) {
      existing.details.push(detail);
    } else {
      grouped.set(item.revenueShareDefinitionId, {
        revenueShareDefinitionId: item.revenueShareDefinitionId,
        clientId: item.clientId,
        partnershipId: item.partnershipId,
        scopes:
          item.scopes?.map(s => ({
            scopeableType: s.scopeableType,
            scopeableId: s.scopeableId ?? null,
          })) ?? [],
        name: item.name,
        applyToPrice: item.applyToPrice,
        effectiveYear: item.effectiveYear,
        status: computeYearStatus(item.effectiveYear),
        details: [detail],
      });
    }
  }

  for (const group of grouped.values()) {
    group.details.sort((a, b) => a.tier - b.tier);
  }

  return Array.from(grouped.values());
};
